import { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  makeStyles
} from '@material-ui/core';
import { db } from 'src/utils/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(6)
  },
  card: {
    padding: theme.spacing(4),
    borderRadius: 16
  }
}));

export default function ExistingScormAccessDirect() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const { schoolId, isClient } = (location.state as any) || {};

  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!schoolId) {
      navigate('/', { replace: true });
      return;
    }

    fetchFiles(schoolId, isClient === true);
  }, [schoolId, isClient]);

  const formatDate = (ts: string | number) => {
    const d = new Date(Number(ts) * 1000);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  const fetchFiles = async (school_id: string, is_client: boolean) => {
    try {
      let q;

      if (is_client) {
        q = query(
          collection(db, 'scormfiles'),
          where('clientid', '==', school_id)
        );
      } else {
        q = query(
          collection(db, 'scormfiles'),
          where('school', '==', school_id)
        );
      }

      const querySnapshot = await getDocs(q);

      const results = querySnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...(doc.data() as any)
        }))
        .sort((a: any, b: any) => Number(b.created) - Number(a.created));

      const seen = new Set<string>();
      const filtered: any[] = [];

      for (const item of results) {
        if (!seen.has(item.sku)) {
          seen.add(item.sku);
          filtered.push(item);
        }
      }

      const visibleItems = filtered.filter((item) => item.visible !== false);

      setFiles(visibleItems);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    throw error;
  }

  return (
    <div style={{ maxWidth: '99vw', margin: '0 auto' }}>
      <Container className={classes.root} maxWidth={false}>
        <Paper elevation={3} className={classes.card} style={{ padding: 24 }}>
          <Typography variant="h4" style={{ fontWeight: 700 }}>
            Download SCORM Connection Files for {schoolId}
          </Typography>

          <Typography
            variant="subtitle2"
            color="textSecondary"
            style={{ marginBottom: 24 }}
          >
            Showing all SCORM records
          </Typography>

          {loading && (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          )}

          {!loading && files.length === 0 && (
            <Box mt={3}>
              <Typography color="textSecondary">No files found.</Typography>
            </Box>
          )}

          {!loading && files.length > 0 && (
            <List>
              {files.map((file) => (
                <ListItem key={file.id} divider>
                  <ListItemText
                    primary={
                      <a
                        href={file.s3_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', fontWeight: 600 }}
                      >
                        {file?.title
                          ?.replace(/_/g, ' ')
                          .replace(/\b(20\d{2}-\d{2})\b/g, '($1)')
                          .replace(/\b(20\d{2})\b(?!-\d{2})/g, ',($1)')
                          .replace('((', '(')
                          .replace('))', ')')
                          .replace(' (,(', ', (')
                          .replace(' ,(', ', (')
                          .trim() || 'Untitled File'}
                        {` [${file?.sku}]`}
                      </a>
                    }
                    secondary={
                      <>
                        <strong>Created:</strong> {formatDate(file.created)} by{' '}
                        <strong>{file?.creator}</strong>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Container>
    </div>
  );
}
